import { useState, useEffect } from 'react';
import { LoadingButton } from "@mui/lab";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Stack,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
} from "@mui/material";
import { MobileDatePicker } from '@mui/x-date-pickers-pro';
// hooks
import useResponsive from '../../hooks/useResponsive';
// componenets
import Iconify from '../../components/Iconify';
import { fYYYYMMDD } from '../../utils/formatTime';


export default function ModifyCareerDialog({ isOpen, closeDialog, modifyCareer, removeCareer, career, careerIndex, hasWorkingCareer, canHide=true }) {
  const smUp = useResponsive('up', 'sm');

  const [companyName, setCompanyName] = useState(career?.company_name || '');
  const [companyNameError, setCompanyNameError] = useState(false);

  const [careerStartDate, setCareerStartDate] = useState(career?.start_date || '');
  const [noStartDateError, setNoStartDateError] = useState(false);

  const [careerEndDate, setCareerEndDate] = useState(career?.end_date || '');
  const [noEndDateError, setNoEndDateError] = useState(false);

  const [isCurrent, setIsCurrent] = useState(career?.end_date === null || false);
  const [hidePeriod, setHidePeriod] = useState(career?.is_hidden_career_period === true || false);

  const [careerDescription, setCareerDescription] = useState('');

  const [checkRemove, setCheckRemove] = useState(false);

  const handleCompanyName = (e) => {
    setCompanyName(e.target.value);
    setCompanyNameError(!e.target.value);
  };

  const handleIsCurrent = (e) => {
    setCareerEndDate('');
    setIsCurrent(e.target.checked);
  };

  const handleHidePeriod = (e) => {
    setHidePeriod(e.target.checked);
  }

  const handleStartDate = (date) => {
    setCareerStartDate(date);
    setNoStartDateError(!date);
  }

  const handleEndDate = (date) => {
    setCareerEndDate(date);
    setNoEndDateError(!date);
  }

  const close = async () => {
    setCompanyName('');
    setCompanyNameError(false);

    setCareerStartDate('');
    setNoStartDateError(false);

    setCareerEndDate('');
    setNoEndDateError(false);

    setIsCurrent(false);
    setHidePeriod(false);

    setCareerDescription('');

    setCheckRemove(false);

    closeDialog();
  };

  const remove = async () => {
    removeCareer({
      index: careerIndex,
    })
    close();
  }

  const modify = async () => {
    if (!companyName || !careerStartDate || (!isCurrent && !careerEndDate)) {
      setCompanyNameError(!companyName);
      setNoStartDateError(!careerStartDate);
      setNoEndDateError(!careerEndDate && !isCurrent);
      return ;
    }

    modifyCareer({
      index: careerIndex,
      company_name: companyName,
      start_date: fYYYYMMDD(careerStartDate) || careerStartDate,
      end_date: isCurrent ? null : (fYYYYMMDD(careerEndDate) || careerEndDate),
      is_hidden_career_period: hidePeriod,
      description: careerDescription,
    })
    close();
  }

  useEffect(() => {
    const initialize = async () => {
      try {
        const startDate = new Date(career?.start_date);
        const startDateTZCorrection = new Date(startDate.getTime() + (startDate.getTimezoneOffset() > 0 ? startDate.getTimezoneOffset() * 60000 : 0));
        setCareerStartDate(startDateTZCorrection || '');

        if (career?.end_date !== null) {
          const endDate = new Date(career?.end_date);
          const endDateTZCorrection = new Date(endDate.getTime() + (endDate.getTimezoneOffset() > 0 ? endDate.getTimezoneOffset() * 60000 : 0));
          setCareerEndDate(endDateTZCorrection || '');
        } else {
          setCareerEndDate('');
        }
        setCompanyName(career?.company_name || '');
        setIsCurrent(career?.end_date === null || false);
        setHidePeriod(career?.is_hidden_career_period === true || false);
        setCareerDescription(career?.description || '');
      } catch (err) {
        console.error(err);
      }
    };

    if(isOpen) initialize();
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onClose={close} fullWidth maxWidth="sm">
      {checkRemove && (
      <>
        <DialogContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Iconify icon={"eva:alert-triangle-fill"} sx={{ width: 20, height: 20, color: 'error.main' }} />
            <Typography variant="subtitle2" sx={{ color: "error.main" }}>
              잠깐, 꼭 확인해주세요
            </Typography>
          </Stack>
          <Typography variant={smUp ? "h5" : "subtitle1"} gutterBottom>
            한번 삭제하면 다시 되돌릴 수 없어요.
          </Typography>
          <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }}>
            경력을 삭제하시겠어요?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Stack direction={{ xs: "column-reverse", sm: "row" }} sx={{ width: 1 }} spacing={1} justifyContent="flex-end">
          <Button
            onClick={() => setCheckRemove(false)}
            size="large"
            sx={{ px: 6 }}
          >
            취소
          </Button>
          <Button
            variant="contained"
            onClick={remove}
            size="large"
            sx={{ px: 6 }}
          >
            삭제
          </Button>
          </Stack>
        </DialogActions>
      </>
      )}
      {!checkRemove && (
      <>
      <DialogTitle sx={{ mb: 1 }}>
        경력 사항
      </DialogTitle>
      <DialogContent>
        <DialogContentText paragraph>
          재직한 혹은 재직 중인 회사 이름 및 년도와 월을 작성해주세요.
        </DialogContentText>
        <Stack spacing={3}>
          <Stack spacing={1} sx={{ width: 1 }}>
            <Typography variant="subtitle2">
              회사 이름
            </Typography>
            <TextField
              fullWidth
              value={companyName}
              error={companyNameError}
              helperText={companyNameError ? '회사이름을 입력해주세요' : ''}
              onChange={handleCompanyName}
            />
          </Stack>
          <Stack direction="row" spacing={2} >
            <Stack spacing={1} sx={{ width: 1 }}>
              <Typography variant="subtitle2">
                시작 연월
              </Typography>
              <MobileDatePicker
                closeOnSelect
                views={['year', 'month']}
                openTo={'year'}
                inputFormat="yyyy년 M월"
                value={careerStartDate}
                onChange={handleStartDate}
                minDate={new Date('1990-01-01')}
                maxDate={careerEndDate || new Date()}
                disableFuture
                renderInput={(params) => (
                  <TextField {...params} fullWidth error={noStartDateError} helperText={noStartDateError ? "날짜를 선택해주세요" : ""} />
                )}
              />
            </Stack>
            <Stack spacing={1} sx={{ width: 1 }}>
              <Typography variant="subtitle2">
                종료 연월
              </Typography>
              {isCurrent && (
                <TextField fullWidth disabled value="재직중" />
              )}
              {!isCurrent && (
              <MobileDatePicker
                closeOnSelect
                views={['year', 'month']}
                openTo={'year'}
                inputFormat="yyyy년 M월"
                value={careerEndDate}
                minDate={careerStartDate}
                maxDate={new Date()}
                onChange={handleEndDate}
                disableFuture
                renderInput={(params) => (
                  <TextField {...params} fullWidth error={noEndDateError} helperText={(noEndDateError && !isCurrent) ? "날짜를 선택해주세요" : ""} />
                )}
                disabled={isCurrent}
              />
              )}
            </Stack>
          </Stack>
          <Stack>
            <FormGroup sx={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
              <FormControlLabel
                control={<Checkbox checked={isCurrent} onClick={handleIsCurrent} disabled={hasWorkingCareer && career.end_date !== null} />}
                label="현재 이 업무로 근무 중"
                sx={{ color: "text.secondary", '& .MuiFormControlLabel-label' : {fontWeight: 'bold' } }}
              />
            </FormGroup>
            {canHide && (
            <FormGroup sx={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
              <FormControlLabel
                control={<Checkbox checked={hidePeriod} onClick={handleHidePeriod} />}
                label="재직기간 비공개"
                sx={{ color: "text.secondary", '& .MuiFormControlLabel-label' : {fontWeight: 'bold' } }}
              />
            </FormGroup>
            )}
            <Stack spacing={1} sx={{ width: 1 }}>
              <Typography variant="subtitle2">
                설명 (선택)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={careerDescription}
                onChange={(e) => setCareerDescription(e.target.value)}
              />
              <Box sx={{ width: 1, textAlign: "end" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {careerDescription?.length === 0 ? "2000자 이내" : `${careerDescription?.length}/2000`}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          sx={{ px: 4 }}
          onClick={() => setCheckRemove(true)}
          size="large"
          color="error"
        >
          경력 삭제
        </LoadingButton>
        <LoadingButton
          sx={{ px: 6 }}
          variant="contained"
          onClick={modify}
          size="large"
          disabled={careerDescription?.length > 2000}
        >
          완료
        </LoadingButton>
      </DialogActions>
      </>
      )}
    </Dialog>
  )
}